import {
  EElement,
  Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {Component, Expressions, Plugins, Types} from '@packages/systems/wfdl';
import {getCurrentComponentType} from '@packages/utilities/platform';
import {NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import {cloneElement} from './cloneElement';
import {unbindReferences} from '@packages/systems/user-components/unbind-references';

/**
 * This function removes all overrides from an element.
 * If an override is found, its value is reset to
 * the corresponding type default.
 */
const removeComponentOverrides = (
  element: ExpressionElement,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType,
  focusedSymbolNativeIdPath: NativeId[]
): ExpressionElement => {
  const currentComponentType = getCurrentComponentType(
    componentMap,
    focusedSymbolNativeIdPath
  );

  const currentComponent = componentMap.get(currentComponentType);
  if (!currentComponent) return element;

  const type = Component.getDataType(currentComponent);
  const fields = Types.getRecordFields(type);
  if (!fields) return element;
  const fieldIds = Object.keys(fields);
  const updatedExpr = fieldIds.reduce(
    (acc: Expression, fieldId: string) =>
      unbindReferences(acc, fieldId, currentComponent, plugins),
    EElement(element)
  );

  return Expressions.getElement(updatedExpr) || element;
};

/**
 * Clones a given element and removes all component property bindings
 */
export const cloneElementWithoutComponentPropertyBindings = (
  element: ExpressionElement,
  components: Component.ComponentMap,
  plugins: Plugins.PluginsType,
  focusedSymbolNativeIdPath: NativeId[],
  seedId: string
) => {
  const stripped = removeComponentOverrides(
    element,
    components,
    plugins,
    focusedSymbolNativeIdPath
  );
  return cloneElement(stripped, components, seedId);
};
